// Which identity provider template a Wallet document is verified with, and which of the person's
// or company's details are filled in for them beforehand. The verification page and the API server
// both choose from here, so a verification is always opened the same way.
//
// Switches are passed in as arguments; nothing here reads the environment or the page. Written as
// plain CommonJS so the API server, which runs its files under plain node, can require this same
// file the browser bundle imports.

const { Country } = require('country-state-city');

const PERSONA_TEMPLATES = Object.freeze({
  // production: "itmpl_2xPSp9xENBt4GzWm7rZo6SfLSdzr",
  production: "itmpl_ythqmFiHHELUWfLfRbUzcbxM8cDn",
  identity: "itmpl_mdgzjwSnk3t8ePuy15r5EdEbLHpj",
  work: "itmpl_Lo7SeG5PRfTZ4iV5sJceVD3mL8nk",
  education: "itmpl_Lo7SeG5PRfTZ4iV5sJceVD3mL8nk",
  experience: "itmpl_Lo7SeG5PRfTZ4iV5sJceVD3mL8nk",
  insurance: "itmpl_Lo7SeG5PRfTZ4iV5sJceVD3mL8nk",
  kyb: "itmpl_LS43fouQZLQhvpU2UJWtFT11SENS",
  // kyc: "itmpl_1sX3MDy4E1K8Qgci56V237t4sbTG",
  kyc: "itmpl_1sX3MDy4E1K8Qgci56V237t4sbTG",
  kycTypeA: "itmpl_ugRxCXUWudB9vKWRUGKe2pCRQ419",
  kycTypeB: "itmpl_FJ8iKu8SASjKpNvtAX6fkKos5h97",
  kycAi: "itmpl_PrFmx83w5oHGpsYwfTWVqwAexiJ8",
  formI20: "itmpl_KWBa73XxUdhCmaE3eP99mNYqAgKA",
  formI94: "itmpl_qS1EeXud9Y1QUjPxckPJRxHKAxyc",
  passportFsmRmi: "itmpl_ht5fyP3UPvnUa71hnod8NkoCpzZ6",
  stateIdCard: "itmpl_ZFoh2Y6JiEJNYLQHVs8y5UHMQMto",
  formI197: "itmpl_tYG3GGdjJeMnjentCuA7UErhCcp2",
  employmentAuthorization: "itmpl_ptfiqvF1sXqfaWT7Bm21WH7WtWHY",
  formI179: "itmpl_Wh9FMrQGozBEnMpKPHxKgHc3GEfZ",
  USBirthCertificate: "itmpl_quJ7tp7GL2euWS8MUdc5nRC7bRPh",
  certificationOfBirthAbroad: "itmpl_5K5TzGdBWRQ8LL722gJfCPxwKnnb",
  socialSecurityCard: "itmpl_JAmESKVSDAjvbYnT7D2JnFjydK13",
  under18AlternativeDocument: "itmpl_YgkpNxQKwXKtYLu4ezmvjJY4K3mc",
  USCGMerchantMarinerCard: "itmpl_J1rkr9xFpHbdmUkEvXrYdoMbuXo4",
  militaryDependentIDCard: "itmpl_exE17ujrj7LhUPnMKMxSTh5srbi6",
  usMilitaryCard: "itmpl_5ujHDXcfExC1iTTqR9tear3EHUKh",
  schoolIDCard: "itmpl_UTbCvM7NhCxh2G4rQKFHUH3tP8Qs",
  formI797: "itmpl_DPvLsv3Q2H6tVR8bcwisud7AbJ68",
  formI24: "itmpl_qS1EeXud9Y1QUjPxckPJRxHKAxyc",
});

/**
 * Helper function to determine the ID type group based on document name.
 * Groups are categorized as follows:
 * - Group A: AAMVA exclusively (USA) - Driver's License, National ID
 * - Group B: Government-issued IDs - Passport, Permanent Resident Card, etc.
 * - Group C: Requires DocAI - Form I-94, School ID, Birth Certificate, etc.
 *
 * @param {string} documentName - The name of the document being verified
 * @returns {string|null} The group identifier ('A', 'B', or 'C') or null if not matched
 */
const idTypeGroupOf = documentName => {
  if (!documentName) return null;

  const normalizedName = documentName.toLowerCase().trim();

  // Group A: AAMVA exclusively (USA)
  const groupA = [
    "driver's license",
    "drivers license",
    "driver license",
    "national id",
    "national identification",
    "state id card",
    "state id",
    "state identification",
  ];

  // Group B: Government-issued IDs
  const groupB = [
    "government id",
    "government identification",
    "u.s. passport",
    "us passport",
    "u.s. passport card",
    "us passport card",
    "passport card",
    "permanent resident card",
    "form i-551",
    "i-551",
    "green card",
    "foreign passport",
    "government-issued id",
    "government issued id",
    "federal id",
    "local id",
    "canadian driver's license",
    "canadian drivers license",
    "canadian driver license",
    "employment authorization document",
    "form i-766",
    "i-766",
    "ead",
    "voter's registration card",
    "voters registration card",
    "voter registration",
    "voter id",
    "native american tribal document",
    "tribal document",
    "canada tribal id",
    "tribal id"
  ];

  // Group C: Requires DocAI
  const groupC = [
    "form i-94",
    "i-94",
    "form i-94 with foreign passport",
    "form i-94 (with foreign passport)",
    "form i-797",
    "i-797",
    "passport from fsm",
    "passport from rmi",
    "fsm passport",
    "rmi passport",
    "passport from fsm or rmi with form i-94",
    "school id card",
    "school id",
    "school identification",
    "school id card with photograph",
    "military dependent's id",
    "military dependents id",
    "military dependent id",
    "military dependent's id card",
    "u.s. coast guard merchant mariner card",
    "us coast guard merchant mariner card",
    "merchant mariner card",
    "coast guard card",
    "under 18 alternatives",
    "school records",
    "clinic records",
    "daycare records",
    "social security card",
    "social security",
    "ssn card",
    "social security card (unrestricted)",
    "certification of birth abroad",
    "form ds-1350",
    "ds-1350",
    "form fs-545",
    "fs-545",
    "form fs-240",
    "fs-240",
    "birth certificate",
    "u.s. birth certificate",
    "us birth certificate",
    "certified copy",
    "form i-179",
    "i-179",
    "resident citizen id",
    "id card for resident citizen",
    "form i-179 (id card for resident citizen)",
    "dhs employment authorization",
    "employment authorization document issued by dhs",
    "employment authorization document",
    "form i-197",
    "i-197",
    "citizen identification card",
    "u.s. citizen identification card",
    "form i-197 (u.s. citizen identification card)",
    "form i-20",
    "i-20",
    "u.s. military card",
    "us military card",
    "military card",
    "draft record",
    "u.s. military card / draft record",
    "us military card / draft record"
  ];

  const inGroup = group => group.some(doc => normalizedName.includes(doc));

  // Check Group A
  if (inGroup(groupA)) {
    return 'A';
  }

  // Check Group B
  if (inGroup(groupB)) {
    return 'B';
  }

  // Check Group C
  if (inGroup(groupC)) {
    return 'C';
  }

  return null;
};

/**
 * Helper function to extract and map currentUser data to Persona prefilling fields.
 * This function maps user profile data to Persona's expected field format for prefilling
 * verification forms, improving user experience by auto-populating known information.
 *
 * Supported prefilling fields:
 * - name-first, name-last: User's first and last name
 * - email-address: User's email address
 * - phone-number: User's phone number
 * - address fields: street, city, state, postal code, country
 * - birthdate: Date of birth in YYYY-MM-DD format
 * - id_type_group: Document group classification (A, B, or C)
 *
 * @param {Object} currentUser - the signed-in user
 * @param {string} [docName] - the name of the document being verified
 * @returns {Object} Mapped fields object for Persona client configuration
 */
const individualPrefillFields = (currentUser, docName) => {

  if (!currentUser?.attributes?.profile) {
    return {};
  }

  const profile = currentUser.attributes.profile;
  const publicData = profile.publicData || {};
  const privateData = profile.privateData || {};

  const fields = {};

  // Basic name fields
  if (profile.firstName) {
    fields['name-first'] = profile.firstName;
  }
  if (profile.lastName) {
    fields['name-last'] = profile.lastName;
  }

  // Email from profile or publicData
  if (currentUser.attributes.email) {
    fields['email-address'] = currentUser.attributes.email;
  }

  // Phone number
  if (publicData.phoneNumber) {
    fields['phone-number'] = publicData.phoneNumber;
  } else if (privateData.phoneNumber) {
    fields['phone-number'] = privateData.phoneNumber;
  }

  // Address fields from publicData
  // Note: The app stores address in two formats:
  // 1. publicData.address.street1/street2 (for street)
  // 2. publicData.cityAddress, stateAddress, zipCode, countryAddress (for city/state/zip/country)
  if (publicData.address) {
    if (publicData.address.street1) {
      fields['address-street-1'] = publicData.address.street1;
    }
    if (publicData.address.street2) {
      fields['address-street-2'] = publicData.address.street2;
    }
  }

  // City, state, zip, and country are stored at the top level of publicData
  if (publicData.cityAddress) {
    fields['address-city'] = publicData.cityAddress;
  }
  if (publicData.stateAddress) {
    fields['address-subdivision'] = publicData.stateAddress;
  }
  if (publicData.zipCode) {
    fields['address-postal-code'] = publicData.zipCode;
  }
  if (publicData.countryAddress) {
    fields['address-country-code'] = publicData.countryAddress;
  }

  // ID type group and document ID based on the document name
  if (docName) {
    const decodedDocName = docName;

    // // Prefill the document name for display in Persona template
    // fields['document_name'] = decodedDocName;

    const idTypeGroup = idTypeGroupOf(decodedDocName);
    if (idTypeGroup) {
      fields['id_type_group'] = idTypeGroup;
    }

    // Pre-select document type in Persona
    const documentId = idClassOf(decodedDocName);
    if (documentId && (idTypeGroup == 'A' || idTypeGroup == 'B')) {
      fields['selected_id_class'] = documentId;
    }

  }

  return fields;
};

/**
 * Helper function to map Group C document names to their specific Persona template IDs.
 * Group C documents require DocAI and each has its own dedicated template.
 *
 * @param {string} documentName - The name of the document being verified
 * @returns {string|null} The Persona template ID for the document or null if not matched
 */
const groupCTemplateOf = documentName => {
  if (!documentName) return null;

  const normalizedName = documentName.toLowerCase().trim();

  // Map document names to their specific template IDs from PERSONA_TEMPLATES
  const templateMapping = {
    // Form I-20
    "form i-20": PERSONA_TEMPLATES.formI20,
    "i-20": PERSONA_TEMPLATES.formI20,

    "form i-94": PERSONA_TEMPLATES.formI94,
    "i-94": PERSONA_TEMPLATES.formI94,

    "passport from fsm or rmi": PERSONA_TEMPLATES.passportFsmRmi,

    "state id": PERSONA_TEMPLATES.stateIdCard,

    // Form I-197
    "form i-197": PERSONA_TEMPLATES.formI197,
    "i-197": PERSONA_TEMPLATES.formI197,
    "u.s. citizen identification card": PERSONA_TEMPLATES.formI197,
    "form i-197 (u.s. citizen identification card)": PERSONA_TEMPLATES.formI197,

    // Employment Authorization Document
    "employment authorization document": PERSONA_TEMPLATES.employmentAuthorization,
    "employment authorization document issued by dhs": PERSONA_TEMPLATES.employmentAuthorization,
    "dhs employment authorization": PERSONA_TEMPLATES.employmentAuthorization,
    "employment authorization document issued by dhs (various visa types)": PERSONA_TEMPLATES.employmentAuthorization,

    // Form I-179
    "form i-179": PERSONA_TEMPLATES.formI179,
    "i-179": PERSONA_TEMPLATES.formI179,
    "resident citizen id": PERSONA_TEMPLATES.formI179,
    "id card for resident citizen": PERSONA_TEMPLATES.formI179,
    "form i-179 (id card for resident citizen)": PERSONA_TEMPLATES.formI179,

    // US Birth Certificate
    "u.s. birth certificate": PERSONA_TEMPLATES.USBirthCertificate,
    "us birth certificate": PERSONA_TEMPLATES.USBirthCertificate,
    "birth certificate": PERSONA_TEMPLATES.USBirthCertificate,
    "u.s. birth certificate (certified copy)": PERSONA_TEMPLATES.USBirthCertificate,
    "certified copy": PERSONA_TEMPLATES.USBirthCertificate,

    // Certification of Birth Abroad
    "certification of birth abroad": PERSONA_TEMPLATES.certificationOfBirthAbroad,
    "form ds-1350": PERSONA_TEMPLATES.certificationOfBirthAbroad,
    "ds-1350": PERSONA_TEMPLATES.certificationOfBirthAbroad,
    "form fs-545": PERSONA_TEMPLATES.certificationOfBirthAbroad,
    "fs-545": PERSONA_TEMPLATES.certificationOfBirthAbroad,
    "form fs-240": PERSONA_TEMPLATES.certificationOfBirthAbroad,
    "fs-240": PERSONA_TEMPLATES.certificationOfBirthAbroad,
    "certification of birth abroad (forms ds-1350, fs-545, fs-240)": PERSONA_TEMPLATES.certificationOfBirthAbroad,

    // Social Security Card
    "social security card": PERSONA_TEMPLATES.socialSecurityCard,
    "social security": PERSONA_TEMPLATES.socialSecurityCard,
    "ssn card": PERSONA_TEMPLATES.socialSecurityCard,
    "social security card (unrestricted)": PERSONA_TEMPLATES.socialSecurityCard,

    // Under 18 Alternatives
    "under 18 alternatives": PERSONA_TEMPLATES.under18AlternativeDocument,
    "school records": PERSONA_TEMPLATES.under18AlternativeDocument,
    "clinic records": PERSONA_TEMPLATES.under18AlternativeDocument,
    "daycare records": PERSONA_TEMPLATES.under18AlternativeDocument,
    "under 18 alternatives (school, clinic, or daycare records)": PERSONA_TEMPLATES.under18AlternativeDocument,

    // US Coast Guard Merchant Mariner Card
    "u.s. coast guard merchant mariner card": PERSONA_TEMPLATES.USCGMerchantMarinerCard,
    "us coast guard merchant mariner card": PERSONA_TEMPLATES.USCGMerchantMarinerCard,
    "merchant mariner card": PERSONA_TEMPLATES.USCGMerchantMarinerCard,
    "coast guard card": PERSONA_TEMPLATES.USCGMerchantMarinerCard,

    // Military Dependent's ID Card
    "military dependent's id": PERSONA_TEMPLATES.militaryDependentIDCard,
    "military dependents id": PERSONA_TEMPLATES.militaryDependentIDCard,
    "military dependent id": PERSONA_TEMPLATES.militaryDependentIDCard,
    "military dependent's id card": PERSONA_TEMPLATES.militaryDependentIDCard,

    // School ID Card
    "school id card": PERSONA_TEMPLATES.schoolIDCard,
    "school id": PERSONA_TEMPLATES.schoolIDCard,
    "school identification": PERSONA_TEMPLATES.schoolIDCard,
    "school id card with photograph": PERSONA_TEMPLATES.schoolIDCard,

    // Form I-797
    "form i-797": PERSONA_TEMPLATES.formI797,
    "i-797": PERSONA_TEMPLATES.formI797,

    // Form I-24
    "form i-24": PERSONA_TEMPLATES.formI24,
    "i-24": PERSONA_TEMPLATES.formI24,

    // U.S. Military Card / Draft Record
    "u.s. military card": PERSONA_TEMPLATES.usMilitaryCard,
    "us military card": PERSONA_TEMPLATES.usMilitaryCard,
    "military card": PERSONA_TEMPLATES.usMilitaryCard,
    "draft record": PERSONA_TEMPLATES.usMilitaryCard,
    "u.s. military card / draft record": PERSONA_TEMPLATES.usMilitaryCard,
    "us military card / draft record": PERSONA_TEMPLATES.usMilitaryCard,
  };

  // Check for exact match first
  if (templateMapping[normalizedName]) {
    return templateMapping[normalizedName];
  }

  // Keyword-based matching for partial matches
  const partialKey = Object.keys(templateMapping).find(
    key => normalizedName.includes(key) || key.includes(normalizedName)
  );
  if (partialKey !== undefined) {
    return templateMapping[partialKey];
  }

  // Fallback to kycAi if no specific template found for Group C document
  return PERSONA_TEMPLATES.kycAi;
};

/**
 * Helper function to map document names to Persona's document ID classes.
 * These IDs are used to pre-select the document type in the Persona verification flow.
 *
 * Based on the actual document list used in the application.
 *
 * @param {string} documentName - The name of the document being verified
 * @returns {string|null} The Persona document ID class or null if not matched
 */
const idClassOf = documentName => {
  if (!documentName) return null;

  const normalizedName = documentName.toLowerCase().trim();

  // Direct mapping based on actual document list (identity & work categories)
  // Maps exact document names to Persona's selected-id-class values
  const exactDocumentMap = {
    // Both Identity & Work Authorization (List A)
    "u.s. passport": "pp",
    "u.s. passport card": "ppc",
    "permanent resident card (form i-551)": "pr",
    "foreign passport (without form i-94)": "pp",
    "driver's license or state id card": "dl",
    "government-issued id card (federal, state, local)": "id",
    "driver's license from canadian authority": "dl",
    "employment authorization document (form i-766)": "wp",
    "voter's registration card (voter id)": "vid",
    "native american tribal document (canada tribal id)": "tribalid",
  };

  // Check for exact match first (case-insensitive)
  if (exactDocumentMap[normalizedName]) {
    return exactDocumentMap[normalizedName];
  }

  // Keyword-based matching for partial matches or variations
  const keywordMap = [
    // U.S. Passport Card - check for passport card first (more specific)
    { keywords: ["passport card", "u.s. passport card", "us passport card"], id: "ppc" },

    // U.S. Passport - separate from passport card
    { keywords: ["u.s. passport", "us passport"], id: "pp" },

    // Permanent Resident Card
    { keywords: ["permanent resident card", "permanent resident", "form i-551", "i-551", "green card"], id: "pr" },

    // Foreign Passport (without Form I-94)
    { keywords: ["foreign passport", "passport"], id: "pp" },

    // Driver's License or State ID
    { keywords: ["driving license", "driver's license", "drivers license", "driver license"], id: "dl" },

    // Government-issued ID
    { keywords: ["government-issued id", "government issued id", "government id", "federal id", "local id"], id: "id" },

    // Driver's License from Canadian Authority
    { keywords: ["canadian driver's license", "canadian drivers license", "canadian driver license", "canadian authority"], id: "dl" },

    // Employment Authorization Document (Form I-766)
    { keywords: ["employment authorization document", "employment authorization", "form i-766", "i-766", "ead"], id: "wp" },

    // Voter's Registration Card
    { keywords: ["voter's registration card", "voters registration card", "voter registration card", "voter registration", "voter id", "voter's id", "voters id"], id: "vid" },

    // Native American Tribal Document
    { keywords: ["native american tribal", "tribal document", "tribal id", "canada tribal id", "canadian tribal"], id: "tribalid" },
  ];

  // Check keyword matches
  const match = keywordMap.find(({ keywords }) =>
    keywords.some(keyword => normalizedName.includes(keyword))
  );

  return match ? match.id : null;
};

/**
 * Helper function to extract and map profileListing data to Persona KYB prefilling fields.
 * This function maps business profile data from profileListing to Persona's expected
 * field format for KYB (Know Your Business) verification flows.
 *
 * Supported KYB prefilling fields:
 * - Business information: name, type, EIN, address
 * - Agency/Customer profile details
 * - Beneficial owners information
 * - Key management personnel
 *
 * NOTE: To make fields read-only/locked:
 * 1. Configure your Persona template in the Dashboard to mark fields as read-only
 * 2. OR remove the field components from inquiry screens (prefilled data will be used but not displayed)
 * 3. OR use server-side inquiry creation via Persona API with locked fields
 *
 * @param {Object} profileListing - the company listing, carrying its private details
 * @param {Object} currentUser - the signed-in user filling in the form
 * @returns {Object} Mapped KYB fields object for Persona client configuration
 */
const businessPrefillFields = (profileListing, currentUser) => {
  if (!profileListing?.attributes?.publicData) {
    return {};
  }

  const publicData = profileListing.attributes.publicData;
  const fields = {};

  // Track emails to detect duplicates across roles
  const emailTracker = new Set();

  // Business name from agencyProfile or customerProfile
  const agencyProfile = publicData.agencyProfile;
  const customerProfile = publicData.customerProfile;


  if (agencyProfile?.name) {
    fields['business_name'] = agencyProfile.name;
    fields['dba_name'] = agencyProfile?.agencyAlternateName;
    fields['business_url'] = publicData?.agencyProfile?.agencyWebsite;
    fields['business_physical_address_street_1'] = publicData?.agencyProfile?.address?.selectedPlace?.address;
    fields['business_physical_address_street_2'] = publicData?.aptSuite;
    fields['business_physical_address_city'] = publicData?.cityAddress;
    fields['business_physical_address_subdivision'] = publicData?.stateAddress;
    fields['business_physical_address_postal_code'] = publicData?.zipCode;

  } else if (customerProfile?.name) {
    fields['business_name'] = customerProfile?.name;
    fields['dba_name'] = customerProfile?.customerAlternateName;
    fields['business_url'] = publicData?.customerProfile?.customerWebsite;
    fields['business_physical_address_street_1'] = publicData?.customerProfile?.address?.selectedPlace?.address;
    fields['business_physical_address_street_2'] = publicData?.aptSuite;
    fields['business_physical_address_city'] = publicData?.cityAddress;
    fields['business_physical_address_subdivision'] = publicData?.stateAddress;
    fields['business_physical_address_postal_code'] = publicData?.zipCode;
  }

  // Business type
  fields['business_type'] = publicData?.onBoardingInfo?.typeOfBusiness?.toLowerCase()?.includes('proprietor') ? "sole_proprietor" : "entity";

  // Match country name from publicData to ISO country code
  if (publicData?.countryAddress) {
    const countryName = publicData.countryAddress;
    // Get all countries and find matching country by name
    const allCountries = Country.getAllCountries();
    const matchedCountry = allCountries.find(
      country => country.name.toLowerCase() === countryName.toLowerCase() ||
        country.isoCode.toLowerCase() === countryName.toLowerCase()
    );

    if (matchedCountry) {
      fields['business_physical_address_country_code'] = matchedCountry.isoCode;
    }
  }

  fields['business_tax_identification_number'] = publicData?.companyIdentification?.identificationNumber;

  // Employee count and revenue
  fields['business_employee_count'] = publicData?.onBoardingInfo?.employeeCount;
  fields['business_annual_revenue'] = publicData?.onBoardingInfo?.annualRevenue;

  // Formation date
  if (publicData?.onBoardingInfo?.dateOfFormation) {
    const formationDate = new Date(publicData.onBoardingInfo.dateOfFormation);
    if (!isNaN(formationDate.getTime())) {
      fields['business_formation_date'] = formationDate.toISOString().split('T')[0];
    }
  }

  // Form filler information
  const formFillerEmail = currentUser?.attributes?.email;
  fields['form_filler_name_first'] = currentUser?.attributes?.profile?.firstName;
  fields['form_filler_name_last'] = currentUser?.attributes?.profile?.lastName;
  fields['form_filler_job_title'] = currentUser?.attributes?.profile?.publicData?.jobTitle;
  fields['form_filler_email_address'] = formFillerEmail;

  // Track form filler email
  if (formFillerEmail) {
    emailTracker.add(formFillerEmail.toLowerCase().trim());
  }


  // Business email
  if (agencyProfile?.email) {
    fields['business-email-address'] = agencyProfile.email;
  } else if (customerProfile?.email) {
    fields['business-email-address'] = customerProfile.email;
  }

  // Beneficial Owners - mapped to ubo_1, ubo_2, etc.
  const beneficialOwners = publicData.beneficialOwners;
  if (beneficialOwners && Array.isArray(beneficialOwners) && beneficialOwners.length > 0) {
    let uboIndex = 1; // Track actual UBO index for fields

    beneficialOwners.forEach((owner, index) => {
      const ownerEmail = owner.email?.toLowerCase().trim();

      // Check if this email has already been used
      if (ownerEmail && emailTracker.has(ownerEmail)) {
        // Check if this person is also a control person
        const authorizedSignatories = publicData.authorizedSignatories;
        const isAlsoControlPerson = authorizedSignatories?.some(
          signatory => signatory.email?.toLowerCase().trim() === ownerEmail
        );

        // If they match the form filler, mark checkbox for "is also owner"
        if (ownerEmail === formFillerEmail?.toLowerCase().trim()) {
          fields['form_filler_is_also_owner'] = true;

          // Prefill the percentage ownership for form filler
          if (owner.ownershipPercentage) {
            fields['form_filler_percentage_ownership'] = owner.ownershipPercentage;
          }

          // Since form filler is ubo_1, next UBO should start at ubo_2
          uboIndex = 2;
        }

        // If they match control person, mark checkbox
        if (isAlsoControlPerson) {
          fields['control_person_is_also_owner'] = true;

          // Prefill the percentage ownership for control person
          if (owner.ownershipPercentage) {
            fields['control_person_percentage_ownership'] = owner.ownershipPercentage;
          }
        }

        return; // Skip prefilling data for this duplicate
      }

      // Track this email and prefill the data
      if (ownerEmail) {
        emailTracker.add(ownerEmail);
      }

      const prefix = `ubo_${uboIndex}`;

      if (owner.firstName) {
        fields[`${prefix}_name_first`] = owner.firstName;
      }
      if (owner.lastName) {
        fields[`${prefix}_name_last`] = owner.lastName;
      }
      if (owner.email) {
        fields[`${prefix}_email_address`] = owner.email;
      }
      if (owner.ownershipPercentage) {
        fields[`${prefix}_percentage_ownership`] = owner.ownershipPercentage;
      }
      if (owner.dateOfBirth) {
        const dob = new Date(owner.dateOfBirth);
        if (!isNaN(dob.getTime())) {
          fields[`${prefix}_birthdate`] = dob.toISOString().split('T')[0];
        }
      }
      if (owner.ssn) {
        fields[`${prefix}_social_security_number`] = owner.ssn;
      }
      if (owner.address) {
        if (owner.address.street1) {
          fields[`${prefix}_address_street_1`] = owner.address.street1;
        }
        if (owner.address.city) {
          fields[`${prefix}_address_city`] = owner.address.city;
        }
        if (owner.address.state) {
          fields[`${prefix}_address_subdivision`] = owner.address.state;
        }
        if (owner.address.zipCode) {
          fields[`${prefix}_address_postal_code`] = owner.address.zipCode;
        }
        if (owner.address.country) {
          fields[`${prefix}_address_country_code`] = owner.address.country;
        }
      }

      // Set add_owner flag for UBO 2 and beyond
      if (uboIndex >= 2) {
        fields[`add_owner_${uboIndex}`] = true;
      }

      uboIndex++; // Increment only when we actually add a UBO
    });
  }

  // Authorized Signatories / Control Person
  // Map up to 2 authorized signatories to control_person_1 and control_person_2
  // For US businesses, 2 associated persons are required by Persona
  const authorizedSignatories = publicData.authorizedSignatories;

  // Helper function to prefill control person fields
  const prefillControlPerson = (person, personIndex) => {
    const prefix = personIndex === 1 ? 'control_person' : `control_person_${personIndex}`;
    const controlPersonEmail = person?.email?.toLowerCase().trim();

    // Check if this email has already been used
    if (controlPersonEmail && emailTracker.has(controlPersonEmail)) {
      // If they match the form filler, mark checkbox for "is also control person"
      if (controlPersonEmail === formFillerEmail?.toLowerCase().trim()) {
        fields['form_filler_is_also_control_person'] = true;
      }

      // Check if this person is also a beneficial owner and mark checkbox if needed
      // This handles the case where control person was already added as a beneficial owner
      const isAlsoBeneficialOwner = beneficialOwners?.some(
        owner => owner.email?.toLowerCase().trim() === controlPersonEmail
      );
      if (isAlsoBeneficialOwner && personIndex === 1) {
        // Only set for first control person (control_person_is_also_owner)
        fields['control_person_is_also_owner'] = true;

        // Find and prefill the percentage ownership for control person
        const ownerData = beneficialOwners.find(
          owner => owner.email?.toLowerCase().trim() === controlPersonEmail
        );
        if (ownerData?.ownershipPercentage) {
          fields['control_person_percentage_ownership'] = ownerData.ownershipPercentage;
        }
      }

      // Don't prefill control person data since it's a duplicate
      return false;
    }

    // Track this email and prefill the data
    if (controlPersonEmail) {
      emailTracker.add(controlPersonEmail);
    }

    if (person.firstName) {
      fields[`${prefix}_name_first`] = person.firstName;
    }
    if (person.lastName) {
      fields[`${prefix}_name_last`] = person.lastName;
    }
    if (person.email) {
      fields[`${prefix}_email_address`] = person.email;
    }
    if (person.title || person.role) {
      fields[`${prefix}_job_title`] = person.title || person.role;
    }

    return true;
  };

  if (authorizedSignatories && Array.isArray(authorizedSignatories) && authorizedSignatories.length > 0) {
    // Process first control person
    const person1 = authorizedSignatories[0];
    if (person1) {
      prefillControlPerson(person1, 1);
    }

    // Process second control person if exists, otherwise send empty strings
    if (authorizedSignatories.length > 1) {
      const person2 = authorizedSignatories[1];
      if (person2) {
        prefillControlPerson(person2, 2);
      }
    } else {
      // No second control person - prefill with empty strings to avoid null values
      // fields['control_person_2_name_first'] = '';
      // fields['control_person_2_name_last'] = '';
      // fields['control_person_2_email_address'] = '';
      // fields['control_person_2_job_title'] = '';
    }
  } else {
    // No control persons at all - prefill both with empty strings
    fields['control_person_name_first'] = '';
    fields['control_person_name_last'] = '';
    fields['control_person_email_address'] = '';
    fields['control_person_job_title'] = '';
    // fields['control_person_2_name_first'] = '';
    // fields['control_person_2_name_last'] = '';
    // fields['control_person_2_email_address'] = '';
    // fields['control_person_2_job_title'] = '';
  }

  return fields;
};

const isEmploymentAuthorizationDocumentName = documentName => {
  const name = (documentName || "").toLowerCase().trim();
  return name.includes("employment authorization document") || name.includes("i-766");
};

// The template for a government ID (Group B) document, chosen from its name. While the EAD
// category check is on, an Employment Authorization Document uses the dedicated EAD template,
// which reads the category printed on the card; otherwise it uses the government ID template.
const governmentIdTemplateFor = (documentName, { eadEnabled } = {}) =>
  eadEnabled && isEmploymentAuthorizationDocumentName(documentName)
    ? PERSONA_TEMPLATES.employmentAuthorization
    : PERSONA_TEMPLATES.kycTypeB;

// The company listing as its own screens hold it: the private details (beneficial owners and tax
// identification) laid over its public data. Without details the listing is used as it is.
const withCompanyDetails = (listing, companyOverlay) => {
  if (!listing || !companyOverlay) return listing;

  const publicData = listing.attributes?.publicData || {};
  return Object.assign({}, listing, {
    attributes: Object.assign({}, listing.attributes, {
      publicData: Object.assign({}, publicData, {
        beneficialOwners: companyOverlay.beneficialOwners || [],
        companyIdentification: Object.assign(
          {},
          publicData.companyIdentification || {},
          companyOverlay.companyIdentification || {}
        ),
      }),
    }),
  });
};

const withoutGroup = fields => {
  const rest = Object.assign({}, fields);
  delete rest.id_type_group;
  return rest;
};

/**
 * The template and pre-filled fields a Wallet document is verified with.
 *
 * A company's documents (and the business verification) use the business template with the
 * company's details. A person's document is matched to its group: Group A (AAMVA) documents use
 * the AAMVA template, Group B (government IDs) the government ID template, or the EAD template
 * while the EAD category check is on, and Group C documents their own document-reading template.
 * Any other document uses the general identity template.
 *
 * @param {Object} args
 * @param {Object} args.item - the Wallet catalog item: its name, and `isKYB`/`subject`
 * @param {Object} args.user - the signed-in user
 * @param {Object} [args.listing] - the subject listing
 * @param {Object} [args.companyOverlay] - the company's private credential details
 * @param {boolean} [args.eadEnabled] - whether the EAD category check is on
 * @returns {{ templateId: string, fields: Object }}
 */
const verificationRequestFor = ({ item, user, listing, companyOverlay, eadEnabled } = {}) => {
  const isKYBFlow = !!item?.isKYB || item?.subject === 'company';
  const docName = item?.name || '';

  if (isKYBFlow) {
    return {
      templateId: PERSONA_TEMPLATES.kyb,
      fields: businessPrefillFields(withCompanyDetails(listing, companyOverlay), user),
    };
  }

  const prefillingFields = individualPrefillFields(user, docName);
  const group = prefillingFields['id_type_group'];
  const normalizedDocName = docName.toLowerCase().trim();

  let templateId = PERSONA_TEMPLATES.kyc;
  let fieldsToSend = prefillingFields;

  if (group === 'A') {
    // AAMVA documents have a dedicated template; a State ID is pre-selected as an ID card.
    templateId = PERSONA_TEMPLATES.kycTypeA;
    fieldsToSend = withoutGroup(fieldsToSend);
    if (docName && normalizedDocName.includes('state id')) {
      fieldsToSend['selected_id_class'] = 'id';
    }
  } else if (group === 'B') {
    templateId = governmentIdTemplateFor(docName, { eadEnabled });
    fieldsToSend = withoutGroup(fieldsToSend);

    if (templateId === PERSONA_TEMPLATES.employmentAuthorization) {
      // Like the other document-reading templates, the EAD template takes only the document name
      fieldsToSend = { document_name: prefillingFields['document_name'] };
    } else if (
      docName &&
      (normalizedDocName.includes('employment authorization document') ||
        normalizedDocName.includes('form i-766') ||
        normalizedDocName.includes('i-766'))
    ) {
      fieldsToSend['selected_id_class'] = 'wp';
    }
  } else if (group === 'C') {
    // Document-reading templates take only the document name
    templateId = docName ? groupCTemplateOf(docName) : PERSONA_TEMPLATES.kycAi;
    fieldsToSend = { document_name: prefillingFields['document_name'] };
  }

  // Group A and B documents (other than a foreign passport) are U.S. documents
  if (
    (group === 'A' || group === 'B') &&
    (templateId === PERSONA_TEMPLATES.kyc ||
      templateId === PERSONA_TEMPLATES.kycTypeA ||
      templateId === PERSONA_TEMPLATES.kycTypeB) &&
    docName &&
    (group === 'A' || !normalizedDocName.includes('foreign passport'))
  ) {
    fieldsToSend['selected_country_code'] = 'US';
  }

  return { templateId, fields: fieldsToSend };
};

module.exports = {
  PERSONA_TEMPLATES,
  idTypeGroupOf,
  groupCTemplateOf,
  idClassOf,
  governmentIdTemplateFor,
  individualPrefillFields,
  businessPrefillFields,
  verificationRequestFor,
};
